import { ChangeEvent, Dispatch, FC, SetStateAction, useRef } from 'react';

import { DOT } from 'utils/constants/common';

import { Cell, CellText, Checkbox, HSpacing, TooltipHover } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { MAX_VACANCY_SELECT } from 'components/MultiCreationAuction/VacanciesTable/VacanciesTable';

import { Vacancy } from 'store/models/multiCreation';
import { useSelector } from 'store/utils';

import styles from 'components/MultiCreationAuction/AddVacanciesModal/AddVacanciesModalTableRow/AddVacanciesModalTableRow.module.less';

const TrlKeys = {
    alreadyAdded: 'MultiCreationAuction.AddVacanciesModal.tip.alreadyAdded',
    id: 'MultiCreationAuction.VacanciesTable.VacanciesTableBody.id',
};

export const AddVacanciesModalTableRow: FC<{
    vacancy: Vacancy;
    selectedVacancyIds: Record<number, boolean>;
    setSelectedVacancyIds: Dispatch<SetStateAction<Record<number, boolean>>>;
}> = ({ vacancy, selectedVacancyIds, setSelectedVacancyIds }) => {
    const vacanciesAuctionInfo = useSelector(({ multiCreation }) => multiCreation.vacanciesAuctionInfo);

    const { vacancyId, name, areaName } = vacancy;

    const activatorRef = useRef<HTMLDivElement>(null);
    const trls = useTrls();

    if (!vacanciesAuctionInfo) {
        return null;
    }

    const handleVacancyCheckboxToggle = (event: ChangeEvent<HTMLInputElement>, vacancyId: number) => {
        const checked = event.target.checked;
        const newSelectedVacanciesIds = { ...selectedVacancyIds };
        if (checked) {
            newSelectedVacanciesIds[vacancyId] = true;
        } else {
            delete newSelectedVacanciesIds[vacancyId];
        }
        setSelectedVacancyIds(newSelectedVacanciesIds);
    };

    const isLimitReached = (vacancyId: number) =>
        Object.keys(selectedVacancyIds).length + Object.keys(vacanciesAuctionInfo).length >= MAX_VACANCY_SELECT &&
        !selectedVacancyIds[vacancyId];

    return (
        <div>
            <Cell
                align="top"
                left={
                    <div ref={activatorRef}>
                        <Checkbox
                            checked={!!selectedVacancyIds[vacancyId] || !!vacanciesAuctionInfo[vacancyId]}
                            onChange={(e) => handleVacancyCheckboxToggle(e, vacancyId)}
                            disabled={isLimitReached(vacancyId) || !!vacanciesAuctionInfo[vacancyId]}
                            data-qa={`add-vacancies-modal-checkbox-${vacancyId}`}
                        />
                    </div>
                }
            >
                <div className={isLimitReached(vacancyId) || !!vacanciesAuctionInfo[vacancyId] ? styles.opacity50 : ''}>
                    <CellText>{name}</CellText>
                    <CellText type="subtitle">
                        {trls(TrlKeys.id, [vacancyId])}
                        <HSpacing default={4} />
                        {DOT}
                        <HSpacing default={4} />
                        {areaName}
                    </CellText>
                </div>
            </Cell>
            {!!vacanciesAuctionInfo[vacancyId] && (
                <TooltipHover activatorRef={activatorRef} size="small" placement="bottom-left">
                    {trls(TrlKeys.alreadyAdded)}
                </TooltipHover>
            )}
        </div>
    );
};
